import React, { useRef, useState, useEffect, forwardRef, useCallback, memo, useMemo } from "react";
import { motion } from "framer-motion";
import { AnnotationCanvas } from "@/components/annotation/AnnotationCanvas";
import { AnnotationStrategy } from "@utils/annotationStrategies/types";
import { AnnotationSource } from "@/types/annotation";
import { useFrame } from "@/contexts/FrameContext";
import { useZoom } from "@/contexts/ZoomContext";
import VideoFilePreview from "./Video";
import ImageFilePreview from "./Image";
import { twClassNames } from "@scripts/react/helpers/twClassNames";
import { useFlow } from "@/contexts/FlowContext";
import { useFilePreview } from "@/contexts/FilePreviewContext";
import { BoundingBox } from "@utils/annotationStrategies/BoundingBox";
import { LoadingOverlay } from "./Loading";
import { useIsWaitingForPredictions } from "@/hooks/useIsWaitingForPredictions";
import { useIsGeneratingPredictions } from "@/hooks/useIsGeneratingPredictions";
import { useStopGeneratingPredictions } from "@/hooks/useStopGeneratingPredictions";
import { BuildSteps, Flows } from "@/types/flows";
import { FileInputDimensions } from "@/types/media";
import { useMediaUtils } from "@/hooks/useMediaUtils";
import { useCurrentMedia } from "@/store";
import { useFlowState } from "@/hooks/useFlowState";

import { LABEL_TOP_PADDING } from "@/components/annotation/colors";

interface FilePreviewProps {
    className?: string;
    strategy?: AnnotationStrategy<BoundingBox>;
    forceHideLabels?: boolean;
    // Props to pass to AnnotationCanvas
    editingAnnotationId?: string | null;
    onAnnotationEditRequest?: (details: any) => void;
    onCanvasClickWithoutAnnotation?: () => void;
    includeWatermark?: boolean;
    watermarkQrUrl?: string;
    // Custom loading overlay
    showProcessingOverlay?: boolean;
    processingOverlayText?: string;
    // Override which data source the canvas renders (predictions vs user annotations)
    annotationSourceOverride?: AnnotationSource;
}

const FilePreview = memo(
    forwardRef<HTMLImageElement | HTMLVideoElement, FilePreviewProps>(
        (
            {
                className,
                strategy,
                forceHideLabels,
                editingAnnotationId,
                onAnnotationEditRequest,
                onCanvasClickWithoutAnnotation,
                showProcessingOverlay,
                processingOverlayText,
                annotationSourceOverride
            },
            ref
        ) => {
            const { flowLocked, setFlowLocked, isExtractingWithAI } = useFlow();

            const {
                mediaRef,
                mediaElementReady,
                handleGetFileDimensions: onGetFileDimensions,
                handleMediaReady: onMediaReady,
                zoomLevel,
                zoomX,
                zoomY
            } = useFilePreview();

            const canvasForFirstFrameRef = useRef<HTMLCanvasElement>(null);

            const [fileUrl, setFileUrl] = useState<string | undefined>();
            // this is maintained to make sure we cleanup the last blob URL on component unmount
            const fileUrlRef = useRef<string | undefined>(undefined);

            const [_areFramePreviewsReady, setAreFramePreviewsReady] = useState(false);
            const [isPlaying, setIsPlaying] = useState(false);
            const [canvasRealignTrigger, setCanvasRealignTrigger] = useState(0);
            const [hoveredAnnotationBounds, setHoveredAnnotationBounds] = useState<{
                x: number;
                y: number;
                width: number;
                height: number;
                svgPath?: string;
            } | null>(null);

            // NEW: State for server frame dimensions
            const [serverFrameDimensions, setServerFrameDimensions] = useState<{
                width: number;
                height: number;
            } | null>(null);

            // Local state for dimensions
            const [localNaturalDimensions, setLocalNaturalDimensions] =
                useState<FileInputDimensions>(null);
            const [localRenderedDimensions, setLocalRenderedDimensions] =
                useState<FileInputDimensions>(null);

            const mediaHolderRef = useRef<HTMLDivElement>(null);

            // Use zoom context values when props are not provided
            const zoomContext = useZoom();
            const effectiveZoomLevel = zoomLevel !== undefined ? zoomLevel : zoomContext.zoomLevel;
            const effectiveZoomX = zoomX !== undefined ? zoomX : zoomContext.x;
            const effectiveZoomY = zoomY !== undefined ? zoomY : zoomContext.y;

            const gestureStateRef = useRef({
                initialDistance: 0,
                initialZoom: 1,
                initialTouchCenter: { x: 0, y: 0 },
                isGesturing: false,
                lastTouchEvent: null as TouchEvent | null
            });

            const { selectedAnnotationId } = useFrame();
            const { stopGeneratingPredictions } = useStopGeneratingPredictions();
            const { step, flow } = useFlowState();

            const currentMedia = useCurrentMedia();

            const { setCurrentMedia } = useMediaUtils();

            const file = useMemo(() => currentMedia?.file.file, [currentMedia?.file.file]);
            const isVideoFile = useMemo(
                () => currentMedia?.file.isVideo,
                [currentMedia?.file.isVideo]
            );

            // Helper function to trigger canvas realignment
            const triggerCanvasRealign = useCallback(() => {
                setCanvasRealignTrigger((prev) => {
                    return prev + 1;
                });
            }, []);

            const { isWaiting: isWaitingForPredictions, loadingMessage } =
                useIsWaitingForPredictions(isExtractingWithAI);
            const isGeneratingPredictions = useIsGeneratingPredictions();

            const showLoadingOverlay = isWaitingForPredictions;

            // Set up layout change observers for canvas realignment
            const setupLayoutChangeObservers = useCallback(
                (mediaHolder: HTMLDivElement) => {
                    // Set up ResizeObserver to detect when the media container changes size
                    let resizeObserver: ResizeObserver | null = null;
                    if (window.ResizeObserver) {
                        resizeObserver = new ResizeObserver((entries) => {
                            // Debounce rapid resize events
                            requestAnimationFrame(() => {
                                triggerCanvasRealign();
                            });
                        });
                        resizeObserver.observe(mediaHolder);
                    }

                    // Set up MutationObserver to detect style/class changes that might affect layout
                    let mutationObserver: MutationObserver | null = null;
                    if (window.MutationObserver) {
                        mutationObserver = new MutationObserver((mutations) => {
                            let shouldRealign = false;
                            mutations.forEach((mutation) => {
                                if (
                                    mutation.type === "attributes" &&
                                    (mutation.attributeName === "style" ||
                                        mutation.attributeName === "class")
                                ) {
                                    shouldRealign = true;
                                }
                            });

                            if (shouldRealign) {
                                requestAnimationFrame(() => {
                                    triggerCanvasRealign();
                                });
                            }
                        });

                        mutationObserver.observe(mediaHolder, {
                            attributes: true,
                            attributeFilter: ["style", "class"],
                            subtree: true // Also watch child elements
                        });
                    }

                    return { resizeObserver, mutationObserver };
                },
                [triggerCanvasRealign]
            );

            // Handle wheel events for trackpad pinch gestures
            const handleWheel = useCallback(
                (e: WheelEvent) => {
                    // Only handle pinch gestures (Ctrl/Cmd + wheel)
                    if (!e.ctrlKey && !e.metaKey) return;

                    // Disable interactions when flow is locked
                    if (flowLocked) return;

                    e.preventDefault();
                    e.stopPropagation();

                    const container = mediaHolderRef.current;
                    if (!container) return;

                    // Get zoom factor from wheel delta
                    const delta = -e.deltaY;
                    const zoomFactor = 1 + delta * 0.001; // Adjust sensitivity as needed

                    // Get current zoom from effective values
                    const currentZoom = effectiveZoomLevel;
                    const newZoom = Math.max(0.1, Math.min(5.0, currentZoom * zoomFactor));

                    // Get mouse position relative to container for zoom origin
                    const rect = container.getBoundingClientRect();
                    const mouseX = e.clientX - rect.left;
                    const mouseY = e.clientY - rect.top;

                    // Calculate zoom origin offset
                    const containerCenterX = rect.width / 2;
                    const containerCenterY = rect.height / 2;
                    const offsetX = (mouseX - containerCenterX) * (1 - zoomFactor);
                    const offsetY = (mouseY - containerCenterY) * (1 - zoomFactor);

                    // Apply zoom and pan offset using zoom context (bypass props)
                    zoomContext.setZoom(
                        newZoom,
                        effectiveZoomX + offsetX,
                        effectiveZoomY + offsetY
                    );
                },
                [zoomContext, effectiveZoomLevel, effectiveZoomX, effectiveZoomY, flowLocked]
            );

            // Handle touch events for mobile pinch gestures
            const handleTouchStart = useCallback(
                (e: TouchEvent) => {
                    if (e.touches.length === 2) {
                        // Disable interactions when flow is locked
                        if (flowLocked) return;

                        e.preventDefault();

                        const gesture = gestureStateRef.current;
                        const touch1 = e.touches[0];
                        const touch2 = e.touches[1];

                        // Calculate initial distance between touches
                        const dx = touch1.clientX - touch2.clientX;
                        const dy = touch1.clientY - touch2.clientY;
                        gesture.initialDistance = Math.sqrt(dx * dx + dy * dy);

                        // Calculate initial touch center
                        gesture.initialTouchCenter = {
                            x: (touch1.clientX + touch2.clientX) / 2,
                            y: (touch1.clientY + touch2.clientY) / 2
                        };

                        gesture.initialZoom = effectiveZoomLevel;
                        gesture.isGesturing = true;
                        gesture.lastTouchEvent = e;
                    }
                },
                [effectiveZoomLevel, flowLocked]
            );

            const handleTouchMove = useCallback(
                (e: TouchEvent) => {
                    if (e.touches.length === 2 && gestureStateRef.current.isGesturing) {
                        // Disable interactions when flow is locked
                        if (flowLocked) return;

                        e.preventDefault();

                        const gesture = gestureStateRef.current;
                        const touch1 = e.touches[0];
                        const touch2 = e.touches[1];

                        // Calculate current distance
                        const dx = touch1.clientX - touch2.clientX;
                        const dy = touch1.clientY - touch2.clientY;
                        const currentDistance = Math.sqrt(dx * dx + dy * dy);

                        // Calculate zoom factor
                        const scale = currentDistance / gesture.initialDistance;
                        const newZoom = Math.max(0.1, Math.min(5.0, gesture.initialZoom * scale));

                        // Calculate current touch center
                        const currentTouchCenter = {
                            x: (touch1.clientX + touch2.clientX) / 2,
                            y: (touch1.clientY + touch2.clientY) / 2
                        };

                        // Calculate pan offset from center movement
                        const panDeltaX = currentTouchCenter.x - gesture.initialTouchCenter.x;
                        const panDeltaY = currentTouchCenter.y - gesture.initialTouchCenter.y;

                        // Apply zoom and pan using zoom context
                        zoomContext.setZoom(
                            newZoom,
                            effectiveZoomX + panDeltaX,
                            effectiveZoomY + panDeltaY
                        );
                        gesture.lastTouchEvent = e;
                    }
                },
                [zoomContext, effectiveZoomX, effectiveZoomY, flowLocked]
            );

            const handleTouchEnd = useCallback((e: TouchEvent) => {
                if (gestureStateRef.current.isGesturing && e.touches.length < 2) {
                    gestureStateRef.current.isGesturing = false;
                    gestureStateRef.current.lastTouchEvent = null;
                }
            }, []);

            // Set up gesture event listeners
            useEffect(() => {
                const container = mediaHolderRef.current;
                if (!container) return;

                const eventListeners: Record<string, (e: any) => void> = {
                    wheel: handleWheel, // Add wheel event listener for trackpad gestures
                    touchstart: handleTouchStart, // Add touch event listeners for mobile gestures
                    touchmove: handleTouchMove,
                    touchend: handleTouchEnd
                };

                Object.entries(eventListeners).forEach(([eventType, handler]) => {
                    container.addEventListener(eventType, handler, { passive: false });
                });

                return () => {
                    Object.entries(eventListeners).forEach(([eventType, handler]) => {
                        container.removeEventListener(eventType, handler);
                    });
                };
            }, [handleWheel, handleTouchStart, handleTouchMove, handleTouchEnd]);

            // Effect to reset local dimensions when file changes
            useEffect(() => {
                setLocalNaturalDimensions(null);
                setLocalRenderedDimensions(null);
                setServerFrameDimensions(null);
            }, [currentMedia?.file.id]);

            // Effect to set up layout change detection for canvas realignment
            useEffect(() => {
                const mediaHolder = mediaHolderRef.current;
                if (!mediaHolder) return;

                const { resizeObserver, mutationObserver } =
                    setupLayoutChangeObservers(mediaHolder);

                return () => {
                    if (resizeObserver) {
                        resizeObserver.disconnect();
                    }
                    if (mutationObserver) {
                        mutationObserver.disconnect();
                    }
                };
            }, [setupLayoutChangeObservers]);

            // Effect to call onGetFileDimensions when both dimensions are available
            useEffect(() => {
                if (localNaturalDimensions && localRenderedDimensions) {
                    onGetFileDimensions?.({
                        natural: localNaturalDimensions,
                        rendered: localRenderedDimensions
                    });
                }
            }, [localNaturalDimensions, localRenderedDimensions, onGetFileDimensions]);

            const revokeFileUrl = (replacement?: string) => {
                if (fileUrlRef.current) {
                    URL.revokeObjectURL(fileUrlRef.current);
                }
                if (replacement) fileUrlRef.current = replacement;
            };

            useEffect(() => {
                // Reset all file-related states when file changes
                setAreFramePreviewsReady(false);
                setServerFrameDimensions(null);

                if (file) {
                    let newUrl = URL.createObjectURL(file);
                    revokeFileUrl(newUrl);
                    setFileUrl(newUrl);
                } else {
                    // reset current media when no file
                    revokeFileUrl();
                    setFileUrl(undefined);
                    setCurrentMedia(undefined);
                }
            }, [file, setCurrentMedia]);

            // ensure we always clean up blob URL on unmount
            useEffect(() => {
                return () => {
                    revokeFileUrl();
                };
            }, []);

            if (!file || !fileUrl) return null;

            return (
                <motion.div
                    className={twClassNames(
                        "filePreview justify-center overflow-hidden",
                        isVideoFile && "hasVideoControls",
                        className
                    )}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15, ease: "easeInOut" }}
                >
                    <div ref={mediaHolderRef} className="relative h-full w-full overflow-hidden">
                        {isVideoFile ? (
                            <VideoFilePreview
                                forwardedRef={mediaRef as React.Ref<HTMLVideoElement>}
                                fileUrl={fileUrl}
                                onMediaReady={onMediaReady}
                                effectiveZoomLevel={effectiveZoomLevel}
                                effectiveZoomX={effectiveZoomX}
                                effectiveZoomY={effectiveZoomY}
                                localNaturalDimensions={localNaturalDimensions}
                                setLocalNaturalDimensions={setLocalNaturalDimensions}
                                localRenderedDimensions={localRenderedDimensions}
                                setLocalRenderedDimensions={setLocalRenderedDimensions}
                                setAreFramePreviewsReady={setAreFramePreviewsReady}
                                isPlaying={isPlaying}
                                setIsPlaying={setIsPlaying}
                            />
                        ) : (
                            <ImageFilePreview
                                forwardedRef={mediaRef as React.Ref<HTMLImageElement>}
                                fileUrl={fileUrl}
                                file={file!}
                                onMediaReady={onMediaReady}
                                effectiveZoomLevel={effectiveZoomLevel}
                                effectiveZoomX={effectiveZoomX}
                                effectiveZoomY={effectiveZoomY}
                                localNaturalDimensions={localNaturalDimensions}
                                setLocalNaturalDimensions={setLocalNaturalDimensions}
                                localRenderedDimensions={localRenderedDimensions}
                                setLocalRenderedDimensions={setLocalRenderedDimensions}
                                serverFrameDimensions={serverFrameDimensions}
                            />
                        )}
                        {/* Dimming overlay when hovering over an annotation - dims everything except the hovered box */}
                        {hoveredAnnotationBounds &&
                            mediaRef?.current &&
                            mediaHolderRef?.current &&
                            (() => {
                                const mediaElement = mediaRef.current;
                                const container = mediaHolderRef.current;
                                if (!mediaElement || !container) return null;

                                const mediaRect = mediaElement.getBoundingClientRect();
                                const containerRect = container.getBoundingClientRect();

                                // Calculate the position of the media element relative to the container
                                const mediaOffsetX = mediaRect.left - containerRect.left;
                                const mediaOffsetY = mediaRect.top - containerRect.top;

                                return (
                                    <svg
                                        className="pointer-events-none absolute transition-opacity duration-150"
                                        style={{
                                            left: mediaOffsetX,
                                            top: mediaOffsetY,
                                            width: mediaRect.width,
                                            height: mediaRect.height
                                        }}
                                        viewBox={`0 0 ${mediaRect.width} ${mediaRect.height}`}
                                    >
                                        <defs>
                                            <mask id="annotation-cutout-mask">
                                                {/* White rectangle covers everything */}
                                                <rect width="100%" height="100%" fill="white" />
                                                {/* Black shape creates a hole for the hovered annotation */}
                                                {hoveredAnnotationBounds.svgPath ? (
                                                    <path
                                                        d={hoveredAnnotationBounds.svgPath}
                                                        fill="black"
                                                    />
                                                ) : (
                                                    <rect
                                                        x={hoveredAnnotationBounds.x}
                                                        y={hoveredAnnotationBounds.y}
                                                        width={hoveredAnnotationBounds.width}
                                                        height={hoveredAnnotationBounds.height}
                                                        fill="black"
                                                    />
                                                )}
                                            </mask>
                                        </defs>
                                        {/* Semi-transparent overlay with cutout mask */}
                                        <rect
                                            width="100%"
                                            height="100%"
                                            fill="black"
                                            opacity="0.5"
                                            mask="url(#annotation-cutout-mask)"
                                        />
                                    </svg>
                                );
                            })()}
                        {file &&
                            mediaElementReady &&
                            strategy &&
                            mediaHolderRef.current &&
                            mediaRef && (
                                <AnnotationCanvas<BoundingBox>
                                    key={file ? `canvas-${file.name}` : "no-canvas"}
                                    mediaRef={
                                        mediaRef as React.RefObject<
                                            HTMLImageElement | HTMLVideoElement
                                        >
                                    }
                                    containerRef={mediaHolderRef as React.RefObject<HTMLDivElement>}
                                    strategy={strategy}
                                    className="z-10"
                                    forceHideLabels={forceHideLabels}
                                    zoomLevel={effectiveZoomLevel}
                                    zoomX={effectiveZoomX}
                                    zoomY={effectiveZoomY}
                                    editingAnnotationId={editingAnnotationId}
                                    selectedAnnotationIdFromContext={selectedAnnotationId}
                                    onAnnotationEditRequest={onAnnotationEditRequest}
                                    onCanvasClickWithoutAnnotation={onCanvasClickWithoutAnnotation}
                                    serverMediaDimensions={serverFrameDimensions || undefined}
                                    currentFrameImageUrl={null}
                                    forceRealign={canvasRealignTrigger}
                                    onHoverChange={setHoveredAnnotationBounds}
                                    annotationSourceOverride={annotationSourceOverride}
                                />
                            )}
                    </div>
                    {/* Loader handled by floating hint for review step */}
                    {/* Show simple loading overlay for build flow (except review) */}
                    {/* Note: improve step manages its own overlay via VisualAreaContent */}
                    {flow === Flows.BUILD && step !== BuildSteps.REVIEW && (
                        <LoadingOverlay
                            show={showLoadingOverlay}
                            loadingText={loadingMessage}
                            onStop={isGeneratingPredictions ? stopGeneratingPredictions : undefined}
                        />
                    )}
                    {/* Custom processing overlay for deploy/share flows */}
                    {showProcessingOverlay && (
                        <LoadingOverlay
                            show={true}
                            loadingText={processingOverlayText || "Processing..."}
                        />
                    )}
                    <canvas ref={canvasForFirstFrameRef} className="hidden" />
                </motion.div>
            );
        }
    )
);

FilePreview.displayName = "FilePreview";

export default FilePreview;
